import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Input, Select, Card, Tag, Tooltip,
  Row, Col, Badge, Popconfirm, message, Modal, Form, Input as AntInput, Divider,
} from 'antd'
import {
  PlusOutlined, EditOutlined, EyeOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SearchOutlined, ReloadOutlined, FileTextOutlined,
  ThunderboltOutlined, AppstoreOutlined, ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { MeterReadingStatus } from '@/types'
import { MeterReadingRow, meterReadingStore } from './meterReadingStore'
import { useModulePermission } from '@/hooks/useModulePermission'
import { useMasterData } from '@/context/MasterDataContext'
import { authStore } from '@/context/auth'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import './MeterReading.css'

const SLOT_COLORS: Record<string, string> = { 'off-peak': '#8B5CF6', 'normal': '#2563EB', 'peak': '#DC2626' }
const SLOT_CAT:    Record<string, string> = { 'off-peak': 'Off-Peak', 'normal': 'Normal', 'peak': 'Peak' }

// Slot codes are named C1, C2, C3 … — sort by the numeric suffix so they
// always display in slot order (C1 → C2 → C3 → …), regardless of the order
// they were saved in or how the Slot Master list happens to be ordered.
const slotSortKey = (code: string): number => {
  const n = parseInt((code || '').replace(/\D/g, ''), 10)
  return isNaN(n) ? Number.MAX_SAFE_INTEGER : n
}

const ENTITY_MAP: Record<string, { name: string; type: string }> = {
  G001: { name: 'WindSolar Energy Pvt Ltd', type: 'generator' },
  G002: { name: 'GreenWind Power Ltd', type: 'generator' },
  G003: { name: 'SolarTech Innovations', type: 'generator' },
  C001: { name: 'ABC Textiles Ltd', type: 'consumer' },
  C002: { name: 'PQR Engineering Pvt Ltd', type: 'consumer' },
  C003: { name: 'LMN Foods & Beverages', type: 'consumer' },
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  [MeterReadingStatus.DRAFT]:       { color: 'default',   icon: <FileTextOutlined />,       label: 'Draft' },
  [MeterReadingStatus.SUBMITTED]:   { color: 'processing', icon: <ClockCircleOutlined />,     label: 'Submitted' },
  [MeterReadingStatus.UNDER_REVIEW]:{ color: 'warning',   icon: <ExclamationCircleOutlined />,label: 'Under Review' },
  [MeterReadingStatus.APPROVED]:    { color: 'success',   icon: <CheckCircleOutlined />,     label: 'Approved' },
  [MeterReadingStatus.REJECTED]:    { color: 'error',     icon: <CloseCircleOutlined />,     label: 'Rejected' },
}

/* ── Component ────────────────────────────────────────── */
const MeterReadingList: React.FC = () => {
  const navigate = useNavigate()
  const { canApprove, canReject } = useModulePermission('Allocations')
  const { generators: masterGens, consumers: masterCons, slots: masterSlots } = useMasterData()
  const isAdmin = authStore.isAdmin()

  // Real Slot Master (not a hardcoded mock) so every configured slot — not
  // just C1–C3 — gets its proper label/color, always shown in C1→C2→C3…
  // order.
  const SLOT_MASTER = masterSlots
    .filter(s => s.status === 'active')
    .slice()
    .sort((a, b) => slotSortKey(a.slotCode) - slotSortKey(b.slotCode))
    .map(s => ({
      name:  s.slotCode,
      label: `${s.slotCode} (${SLOT_CAT[s.category] ?? s.category})`,
      from:  s.startTime,
      to:    s.endTime,
      color: SLOT_COLORS[s.category] ?? '#4A5568',
    }))

  const [readings, setReadings] = useState<MeterReadingRow[]>(() => meterReadingStore.getAll())
  const [search, setSearch]           = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterType, setFilterType]   = useState<string>('')
  const [pendingOnly, setPendingOnly] = useState(false)
  const [rejectModal, setRejectModal] = useState<{ open: boolean; id: string }>({ open: false, id: '' })
  const [rejectForm] = Form.useForm()

  /* Hydrate from the DB on mount and stay live on any add / edit / status
     change. Previously this only read the in-memory cache once on mount —
     on a fresh page load the cache starts empty and the real data arrives a
     moment later via refresh(), but nothing was listening for that, so this
     page could get stuck showing "0 readings" / stale data indefinitely
     (every other list page in the app already subscribes to its store's
     update event; this one didn't). */
  useEffect(() => {
    const update = () => setReadings(meterReadingStore.getAll())
    update()
    meterReadingStore.refresh().then(update)
    window.addEventListener(meterReadingStore.eventName, update)
    return () => window.removeEventListener(meterReadingStore.eventName, update)
  }, [])

  /* Derived */
  // Newest entries first — the backend has no guaranteed row order (no
  // createdAt column on meter_readings), so without this a freshly
  // submitted reading could land on page 2+ instead of showing up front.
  // submittedAt is stamped with today's date on every save (draft or
  // submit), so sorting by it (with id as a same-day tiebreak) surfaces
  // whatever was just added or edited at the top.
  const getEntryTime = (r: MeterReadingRow): number => {
    const dateStr = r.submittedAt || r.updatedAt || r.createdAt
    if (dateStr) {
      const t = dayjs(dateStr).valueOf()
      if (!isNaN(t) && t > 0) return t
    }
    const num = parseInt(r.id.replace(/\D/g, ''), 10) || 0
    return num
  }

  const filtered = readings
    .filter((r) => {
      const entity = ENTITY_MAP[r.entityId]
      const matchSearch =
        r.id.toLowerCase().includes(search.toLowerCase()) ||
        r.entityId.toLowerCase().includes(search.toLowerCase()) ||
        (entity?.name || '').toLowerCase().includes(search.toLowerCase())
      const matchStatus = pendingOnly
        ? (r.status === MeterReadingStatus.SUBMITTED || r.status === MeterReadingStatus.UNDER_REVIEW)
        : (!filterStatus || r.status === filterStatus)
      const matchType   = !filterType   || r.type   === filterType
      return matchSearch && matchStatus && matchType
    })
    .sort((a, b) => {
      // Order strictly by the date/timestamp the meter reading was entered (newest entered first)
      const timeA = getEntryTime(a)
      const timeB = getEntryTime(b)
      if (timeA !== timeB) return timeB - timeA

      return b.id.localeCompare(a.id)
    })

  /* KPI counts */
  const kpi = {
    total:      readings.length,
    pending:    readings.filter(r => r.status === MeterReadingStatus.SUBMITTED || r.status === MeterReadingStatus.UNDER_REVIEW).length,
    approved:   readings.filter(r => r.status === MeterReadingStatus.APPROVED).length,
    rejected:   readings.filter(r => r.status === MeterReadingStatus.REJECTED).length,
    totalUnits: readings.filter(r => r.status === MeterReadingStatus.APPROVED).reduce((s, r) => s + Number(r.totalUnits), 0),
  }

  const kpiCards = [
    {
      label: 'Total Readings',
      value: kpi.total,
      icon: <AppstoreOutlined style={{ fontSize: 24, color: '#001A4D' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: '#001A4D',
    },
    {
      label: 'Pending Approval',
      value: kpi.pending,
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#F59E0B' }} />,
      iconBg: 'rgba(245,158,11,0.08)',
      borderColor: '#F59E0B',
    },
    {
      label: 'Approved',
      value: kpi.approved,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#00A651' }} />,
      iconBg: 'rgba(0,166,81,0.08)',
      borderColor: '#00A651',
    },
    {
      label: 'Total Units (Approved)',
      value: (
        <>
          {(kpi.totalUnits / 1000).toFixed(1)}
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 4 }}>MWh</span>
        </>
      ),
      icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#001A4D' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: '#001A4D',
    },
  ]

  /* Actions */
  const handleApprove = (id: string) => {
    const approvedAt = new Date().toISOString().slice(0, 10)
    const updatedAt = new Date().toISOString()
    meterReadingStore.save({
      id,
      status: MeterReadingStatus.APPROVED,
      approvedBy: 'Supervisor Kumar',
      approvedAt,
      updatedAt,
    })
    setReadings(prev => prev.map(r => r.id === id
      ? { ...r, status: MeterReadingStatus.APPROVED, approvedBy: 'Supervisor Kumar', approvedAt, updatedAt }
      : r
    ))
    message.success('Reading approved successfully!')
  }

  const handleReject = (values: { reason: string }) => {
    const updatedAt = new Date().toISOString()
    meterReadingStore.save({
      id: rejectModal.id,
      status: MeterReadingStatus.REJECTED,
      rejectionReason: values.reason,
      updatedAt,
    })
    setReadings(prev => prev.map(r => r.id === rejectModal.id
      ? { ...r, status: MeterReadingStatus.REJECTED, rejectionReason: values.reason, updatedAt }
      : r
    ))
    message.error('Reading rejected.')
    setRejectModal({ open: false, id: '' })
    rejectForm.resetFields()
  }

  /* Columns */
  const columns = [
    {
      title: 'Reading ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text: string, r: MeterReadingRow) => (
        <span 
          style={{ fontWeight: 800, color: '#1677ff', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); navigate(`/meter-readings/${r.id}`); }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 170,
      ellipsis: true,
      render: (_: any, r: MeterReadingRow) => {
        const isGen  = r.type === 'generator'
        const masterEntity = isGen
          ? masterGens.find(g => g.id === r.entityId)
          : masterCons.find(c => c.id === r.entityId)
        const name = r.entityName || masterEntity?.name || ENTITY_MAP[r.entityId]?.name || r.entityId
        const sub = `${r.entityId} · ${isGen ? 'Generator' : 'Consumer'}`
        return (
          <Tooltip title={`${name} — ${sub}`}>
            <div className="mr-entity-chip" style={{ overflow: 'hidden' }}>
              <div
                className="mr-entity-avatar"
                style={{ background: isGen ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#001A4D,#2563EB)' }}
              >
                {isGen ? '⚡' : '👤'}
              </div>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <div className="mr-entity-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                <div className="mr-entity-sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
              </div>
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: 'Plant Name',
      key: 'plantName',
      width: 180,
      ellipsis: true,
      render: (_: any, r: MeterReadingRow) => {
        const isGen = r.type === 'generator'
        const htNum = (r as any).htNumber || (isGen
          ? masterGens.find(g => g.id === r.entityId)?.plants?.find(p => p.id === r.plantId)?.htServiceNumber
          : masterCons.find(c => c.id === r.entityId)?.services?.find(s => s.id === r.plantId)?.htscNumber)
        return (
          <Tooltip title={r.plantName || '—'}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <div style={{ fontWeight: 600, color: '#0B1426', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.plantName || '—'}
              </div>
              {htNum && (
                <div style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace' }}>
                  HT: {htNum}
                </div>
              )}
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: 'Period',
      key: 'period',
      width: 140,
      render: (_: any, r: MeterReadingRow) => {
        const start = dayjs(r.startDate)
        const end = dayjs(r.endDate)
        const days = end.diff(start, 'day') + 1
        return (
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0B1426' }}>
              {start.format('DD MMM YYYY')}
            </div>
            <div style={{ fontSize: 11, color: '#8A93A6' }}>
              → {end.format('DD MMM YYYY')} ({days}d)
            </div>
          </div>
        )
      },
    },
    {
      title: 'Total Units',
      key: 'totalUnits',
      width: 120,
      render: (_: any, r: MeterReadingRow) => (
        <div>
          <div className="mr-units">{Number(r.totalUnits || 0).toLocaleString('en-IN')}</div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>kWh</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const cfg = STATUS_CONFIG[status] || { color: 'default', icon: null, label: status }
        return (
          <Tag icon={cfg.icon} color={cfg.color} style={{ borderRadius: 6, fontWeight: 700, fontSize: 12 }}>
            {cfg.label}
          </Tag>
        )
      },
    },
    {
      title: 'Submitted By / Date',
      key: 'submittedBy',
      width: 160,
      sorter: (a: MeterReadingRow, b: MeterReadingRow) => getEntryTime(a) - getEntryTime(b),
      render: (_: any, r: MeterReadingRow) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0B1426' }}>{r.submittedBy || 'System'}</div>
          <div style={{ fontSize: 11.5, color: '#8A93A6' }}>
            {r.submittedAt ? dayjs(r.submittedAt).format('DD MMM YYYY') : (r.createdAt ? dayjs(r.createdAt).format('DD MMM YYYY') : '—')}
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, r: MeterReadingRow) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View Full Details">
            <Button className="mr-action-btn" icon={<EyeOutlined />}
              onClick={(e) => { e.stopPropagation(); navigate(`/meter-readings/${r.id}`); }} />
          </Tooltip>
          <Tooltip title={r.status === MeterReadingStatus.APPROVED && !isAdmin ? "Only Admin can edit approved meter readings" : "Edit Reading"}>
            <Button className="mr-action-btn" icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); navigate(`/meter-readings/${r.id}/edit`); }}
              disabled={r.status === MeterReadingStatus.APPROVED && !isAdmin} />
          </Tooltip>
          {(r.status === MeterReadingStatus.SUBMITTED || r.status === MeterReadingStatus.UNDER_REVIEW) && (
            <>
              {canApprove && (
                <Tooltip title="Approve">
                  <Popconfirm
                    title="Approve Reading?"
                    description="This reading will be used for unit allocation."
                    onConfirm={() => handleApprove(r.id)}
                    okText="Approve" cancelText="Cancel"
                    okButtonProps={{ style: { background: '#00A651', borderColor: '#00A651' } }}
                  >
                    <Button className="mr-action-btn mr-action-btn-approve" icon={<CheckCircleOutlined />} />
                  </Popconfirm>
                </Tooltip>
              )}
              {canReject && (
                <Tooltip title="Reject">
                  <Button className="mr-action-btn mr-action-btn-reject" icon={<CloseCircleOutlined />}
                    onClick={() => setRejectModal({ open: true, id: r.id })} />
                </Tooltip>
              )}
              {!canApprove && !canReject && (
                <Tooltip title="Your role isn't authorized to approve or reject readings">
                  <Tag color="default" style={{ fontSize: 11 }}>Awaiting authorized approver</Tag>
                </Tooltip>
              )}
            </>
          )}
        </Space>
      ),
    },
  ]

  if (!meterReadingStore.loaded) return <TableSkeleton columns={8} />

  return (
    <div className="mr-page">

      {/* ── KPI Strip ──────────────────────────────────────── */}
      <KpiCardRow cards={kpiCards} />

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="mr-header">
        <div className="mr-header-left">
          <h2>⚡ Meter Readings</h2>
          <p>Capture, review and approve slot-wise generation & consumption readings</p>
        </div>
        <div className="mr-header-actions">
          <Button icon={<ReloadOutlined />} onClick={() => { setReadings(meterReadingStore.getAll()); message.info('Refreshed') }} />
          <Button type="primary" icon={<PlusOutlined />} size="large"
            onClick={() => navigate('/meter-readings/new')}
            style={{ fontWeight: 700 }}>
            New Reading
          </Button>
        </div>
      </div>

      {/* ── Filter Strip ───────────────────────────────────── */}
      <Card className="mr-filter-card" style={{ marginBottom: 14, borderRadius: 12, border: '1px solid #E4E8F0' }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search by Reading ID, Entity..."
              prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Type"
              allowClear
              value={filterType || undefined}
              onChange={setFilterType}
              style={{ width: '100%' }}
              options={[
                { label: '⚡ Generator', value: 'generator' },
                { label: '👤 Consumer', value: 'consumer' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Status"
              allowClear
              disabled={pendingOnly}
              value={filterStatus || undefined}
              onChange={(v) => { setFilterStatus(v); setPendingOnly(false) }}
              style={{ width: '100%' }}
              options={Object.values(MeterReadingStatus).map(s => ({ label: s, value: s }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button block onClick={() => { setSearch(''); setFilterStatus(''); setFilterType(''); setPendingOnly(false) }}>
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* ── Results info ───────────────────────────────────── */}
      <div style={{ fontSize: 12, color: '#8A93A6', marginBottom: 8, textAlign: 'right' }}>
        Showing <strong>{filtered.length}</strong> of <strong>{readings.length}</strong> readings
        {kpi.pending > 0 && (
          <Tooltip title={pendingOnly ? 'Showing only readings awaiting approval — click to show all' : 'Click to show only readings awaiting approval'}>
            <span
              onClick={() => setPendingOnly(p => !p)}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            >
              <Badge count={kpi.pending} style={{ marginLeft: 10, backgroundColor: '#F59E0B' }} />
              <span
                style={{
                  marginLeft: 4, color: '#D97706', fontWeight: 700,
                  textDecoration: pendingOnly ? 'underline' : undefined,
                }}
              >
                readings awaiting approval
              </span>
            </span>
          </Tooltip>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────── */}
      <Card className="mr-table-card">
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          tableLayout="fixed"
          scroll={{ x: 1150 }}
          rowClassName={(r) => r.status === MeterReadingStatus.REJECTED ? 'ant-table-row-disabled' : ''}
          onRow={(record) => ({
            onClick: () => navigate(`/meter-readings/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} readings`,
          }}
          summary={(pageData) => {
            const total = pageData.reduce((s, r) => s + Number(r.totalUnits), 0);
            return (
              <Table.Summary.Row className="mr-summary-row">
                <Table.Summary.Cell index={0} colSpan={4}>
                  <span className="mr-summary-label">⚡ Total Units</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <span className="mr-summary-value">
                    {total.toLocaleString('en-IN')} kWh
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={3} />
              </Table.Summary.Row>
            )
          }}
          expandable={{
            expandedRowRender: (r) => {
              // Find chronological previous reading (ends before current starts) for the same entity and type
              const priorReading = readings
                .filter(other =>
                  other.id !== r.id &&
                  other.entityId === r.entityId &&
                  other.type === r.type &&
                  dayjs(other.endDate).isBefore(dayjs(r.startDate))
                )
                .sort((a, b) => dayjs(b.endDate).diff(dayjs(a.endDate)))[0];

              // No fake baseline when there's no real prior reading — the
              // variance/slot-share panels below render an honest "no data"
              // state instead of comparing against a fabricated number.
              const priorUnits = priorReading ? priorReading.totalUnits : null;
              const pctChange = priorUnits && priorUnits > 0 ? ((r.totalUnits - priorUnits) / priorUnits) * 100 : null;

              return (
                <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: 8 }}>
                  <Row gutter={[16, 16]}>
                    {/* Left: Slot-wise Grid */}
                    <Col xs={24} lg={14}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#001A4D', marginBottom: 12 }}>
                        Current Slot Distribution ({dayjs(r.startDate).format('DDMMMYYYY')} - {dayjs(r.endDate).format('DDMMMYYYY')})
                      </div>
                      <Row gutter={[12, 12]}>
                        {[...r.slots].sort((a, b) => slotSortKey(a.slotName) - slotSortKey(b.slotName)).map(s => {
                          const def = SLOT_MASTER.find(m => m.name === s.slotName)
                          const pct = r.totalUnits > 0 ? ((s.units / r.totalUnits) * 100).toFixed(1) : '0'
                          return (
                            <Col key={s.slotName} xs={24} sm={8}>
                              <div style={{ background: '#fff', border: '1px solid #E4E8F0', borderRadius: 8, padding: '10px 14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                  <span style={{ fontWeight: 800, color: def?.color || '#4A5568', fontSize: 13 }}>
                                    {def?.label || s.slotName}
                                  </span>
                                  <span style={{ fontSize: 11, color: '#8A93A6' }}>{def?.from}–{def?.to}</span>
                                </div>
                                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 800, color: '#0B1426' }}>
                                  {s.units.toLocaleString('en-IN')} <span style={{ fontSize: 12, fontWeight: 600, color: '#8A93A6' }}>kWh</span>
                                </div>
                                <div style={{ fontSize: 11, color: '#8A93A6', marginTop: 2 }}>{pct}% of total</div>
                                <div style={{ height: 4, background: '#E4E8F0', borderRadius: 2, marginTop: 8 }}>
                                  <div style={{ height: '100%', background: def?.color || '#001A4D', borderRadius: 2, width: `${pct}%` }} />
                                </div>
                              </div>
                            </Col>
                          )
                        })}
                      </Row>
                    </Col>
 
                    {/* Right: MoM Comparison Card */}
                    <Col xs={24} lg={10}>
                      <div style={{ background: '#fff', border: '1px solid #E4E8F0', borderRadius: 8, padding: '14px 16px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#001A4D', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>📊</span> Historical Consumption Comparison
                          </div>
                          <Row gutter={8}>
                            <Col span={12}>
                              <div style={{ fontSize: 10.5, color: '#8A93A6', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                                Current Period
                              </div>
                              <div style={{ fontSize: 11.5, color: '#4A5568', fontWeight: 600, marginTop: 2 }}>
                                {dayjs(r.startDate).format('DDMMMYYYY')} - {dayjs(r.endDate).format('DDMMMYYYY')}
                              </div>
                              <div style={{ fontSize: 16, fontWeight: 800, color: '#0B1426', marginTop: 4 }}>
                                {r.totalUnits.toLocaleString('en-IN')} <span style={{ fontSize: 11, color: '#8A93A6', fontWeight: 500 }}>kWh</span>
                              </div>
                            </Col>
                            <Col span={12} style={{ borderLeft: '1px solid #E4E8F0', paddingLeft: 12 }}>
                              <div style={{ fontSize: 10.5, color: '#8A93A6', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                                Previous Period
                              </div>
                              <div style={{ fontSize: 11.5, color: '#4A5568', fontWeight: 600, marginTop: 2 }}>
                                {priorReading
                                  ? `${dayjs(priorReading.startDate).format('DDMMMYYYY')} - ${dayjs(priorReading.endDate).format('DDMMMYYYY')}`
                                  : 'No prior reading'}
                              </div>
                              <div style={{ fontSize: 16, fontWeight: 800, color: '#8A93A6', marginTop: 4 }}>
                                {priorUnits != null
                                  ? <>{priorUnits.toLocaleString('en-IN')} <span style={{ fontSize: 11, color: '#8A93A6', fontWeight: 500 }}>kWh</span></>
                                  : '—'}
                              </div>
                            </Col>
                          </Row>

                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5568', marginBottom: 6 }}>
                              Previous Period Slot Share{priorReading ? ` (${dayjs(priorReading.startDate).format('MMM YYYY')})` : ''}
                            </div>
                            {priorReading ? (
                              <div style={{ display: 'flex', gap: 8 }}>
                                {[...priorReading.slots].sort((a, b) => slotSortKey(a.slotName) - slotSortKey(b.slotName)).map(ps => {
                                  const def = SLOT_MASTER.find(m => m.name === ps.slotName);
                                  const psPct = priorReading.totalUnits > 0 ? ((ps.units / priorReading.totalUnits) * 100).toFixed(1) : '0';
                                  return (
                                    <div key={ps.slotName} style={{ flex: 1, background: '#F8FAFC', border: '1px solid #E4E8F0', borderRadius: 6, padding: '6px', textAlign: 'center' }}>
                                      <div style={{ fontSize: 10, fontWeight: 700, color: def?.color || '#4A5568' }}>{ps.slotName}</div>
                                      <div style={{ fontSize: 12, fontWeight: 800, color: '#0B1426' }}>{psPct}%</div>
                                      <div style={{ fontSize: 9.5, color: '#8A93A6' }}>{(ps.units/1000).toFixed(1)}k kWh</div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: '#8A93A6', fontStyle: 'italic' }}>
                                No earlier reading on record for this entity yet.
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <Divider style={{ margin: '12px 0' }} />
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 12, color: '#4A5568', fontWeight: 500 }}>Consumption Variance</span>
                            {pctChange != null ? (
                              <span style={{
                                fontWeight: 800,
                                fontSize: 13,
                                color: pctChange >= 0 ? '#059669' : '#DC2626',
                                background: pctChange >= 0 ? '#EFFDF5' : '#FEF2F2',
                                padding: '2px 8px',
                                borderRadius: 6,
                                border: pctChange >= 0 ? '1px solid rgba(5,150,105,0.2)' : '1px solid rgba(220,38,38,0.2)',
                              }}>
                                {pctChange >= 0 ? '▲ +' : '▼ '}
                                {pctChange.toFixed(1)}%
                              </span>
                            ) : (
                              <span style={{ fontSize: 12, color: '#8A93A6' }}>— (no prior reading)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>

                    {r.rejectionReason && (
                      <Col xs={24}>
                        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', color: '#DC2626', fontSize: 13 }}>
                          <strong>Rejection Reason:</strong> {r.rejectionReason}
                        </div>
                      </Col>
                    )}
                    {r.approvedBy && (
                      <Col xs={24}>
                        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 14px', color: '#00A651', fontSize: 13 }}>
                          <strong>Approved by:</strong> {r.approvedBy} on {r.approvedAt ? dayjs(r.approvedAt).format('DDMMMYYYY') : ''}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )
            },
            rowExpandable: () => true,
          }}
        />
      </Card>

      {/* ── Reject Modal ───────────────────────────────────── */}
      <Modal
        title={<span style={{ color: '#DC2626' }}><CloseCircleOutlined style={{ marginRight: 8 }} />Reject Reading</span>}
        open={rejectModal.open}
        onCancel={() => { setRejectModal({ open: false, id: '' }); rejectForm.resetFields() }}
        footer={null}
        width={480}
      >
        <Form form={rejectForm} layout="vertical" onFinish={handleReject}>
          <Form.Item
            name="reason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <AntInput.TextArea
              rows={4}
              placeholder="e.g. Reading values do not match TANGEDCO records..."
            />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button onClick={() => { setRejectModal({ open: false, id: '' }); rejectForm.resetFields() }}>Cancel</Button>
            <Button type="primary" htmlType="submit" danger>Reject Reading</Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default MeterReadingList
